import { FormEvent, useContext, useState } from "react";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Loader } from "@remo-co/ui-core/src/components/Loader";
import { FormControl } from "@remo-co/ui-core/src/components/FormControl";
import { FormHelperText } from "@remo-co/ui-core/src/components/FormHelperText";
import { InputLabel } from "@remo-co/ui-core/src/components/InputLabel";
import { TextField } from "@remo-co/ui-core/src/components/TextField";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ScrollableView } from "@remo-co/ui-core/src/components/ScrollableView";
import logger from "logging/logger";
import { useI18n } from "i18n";
import { Trans } from "react-i18next";
import ModalWindow from "modules/common/modalWindow/ModalWindow";
import { Events } from "modules/tracking";
import { USERPILOT_CONTEXT } from "modules/userpilot/UserpilotContext";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Errors, trackError } from "modules/monitoring";
import { ISubscriptionDetail } from "types/subscription";
import subscriptionApi from "../../Subscription.api";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as HeartBreakIcon } from "./assets/heart_break.svg";
import { useStyles } from "./styles";

interface CancelSubscriptionProps {
  handleClose: (shouldRefreshSubscriptionInfo: boolean) => void;
  subscriptionDetail: ISubscriptionDetail;
}

const NOTIF_AUTO_DISMISS_SECONDS = 10;
const MAX_FEEDBACK_LENGTH = 500;
const MIN_FEEDBACK_LENGTH = 4;

const CancelSubscription = ({
  subscriptionDetail,
  handleClose,
}: CancelSubscriptionProps): JSX.Element => {
  const [userFeedback, setUserFeedback] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const { trackUserpilotEvent } = useContext(USERPILOT_CONTEXT);
  const { t } = useI18n(["common", "settings", "server"]);
  const styles = useStyles();

  const planName = () => subscriptionDetail?.plan?.name?.toLowerCase() ?? "N/A";
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    // Collect data for processing
    const data = {
      userFeedback,
      subscriptionId: subscriptionDetail?.subscription?.id,
      plan: planName(),
    };

    try {
      // Call API to cancel subscription
      const result = await subscriptionApi.cancelUserSubscription(data);
      setIsProcessing(false);
      if (!result.isSuccess) {
        throw new Error(result?.message);
      }
      logger.info(
        `[CancelSubscription] Subscription cancelled for ${subscriptionDetail?.subscription?.id}`,
      );
      addSuccessNotification({
        message: t("settings:subscription.cancelled"),
        autoDismiss: NOTIF_AUTO_DISMISS_SECONDS,
      });
      // Send data to Userpilot
      const trackingData = {
        plan: planName(),
      };

      trackUserpilotEvent(Events.SUBSCRIPTION_CANCELLED, trackingData);
      handleClose(true);
    } catch (error) {
      addErrorNotification({
        message: t("server:unexpected.error"),
        autoDismiss: NOTIF_AUTO_DISMISS_SECONDS,
      });
      const errorMessage = error instanceof Error ? error.message : "";
      logger.error(
        `[CancelSubscription] Unable to cancel subscription ${subscriptionDetail?.subscription?.id}`,
        { error: errorMessage },
      );
      if (error instanceof Object || typeof error === "string") {
        trackError(error, {
          label: Errors.SUBSCRIPTION_CANCEL,
        });
      }
    }
  };

  const characterCount = `${userFeedback.length}/${MAX_FEEDBACK_LENGTH}`;

  const isInvalidFeedback = userFeedback.length < MIN_FEEDBACK_LENGTH;

  return (
    <ModalWindow
      onClose={() => handleClose(false)}
      disableCloseOnBackdrop
      fullScreen={false}
      className={styles.modalWindow}
      open
    >
      <div className={styles.heading}>
        <div className={styles.icon}>
          <HeartBreakIcon id="heart-break-icon" />
        </div>
        <div className={styles.title}>
          <Typography className={styles.titleText}>
            {t("settings:sorry")}
          </Typography>
          <Typography className={styles.subTitleText}>
            {t("settings:feedback")}
          </Typography>
        </div>
      </div>
      <ScrollableView withAppBar={false} className={styles.formScrollView}>
        <form noValidate onSubmit={handleSubmit}>
          <FormControl aria-describedby="cancellation-reason">
            <InputLabel className={styles.feedbackLabel}>
              {t("settings:improve")}
            </InputLabel>
            <TextField
              onChange={(e) => setUserFeedback(e.target.value)}
              multiline
              inputProps={{
                maxLength: MAX_FEEDBACK_LENGTH,
                "data-testid": "feedback-text",
              }}
              rows={4}
              variant="outlined"
            />
            <FormHelperText id="char-count-text">
              {characterCount}
            </FormHelperText>
          </FormControl>
          <div className={styles.infoContainer}>
            <div className={styles.infoIcon}>
              <InfoIcon id="info-icon" />
            </div>
            <div className={styles.infoMessage}>
              <Typography component="span" color="inherit">
                <Trans
                  i18nKey="settings:accounts.cancel"
                  components={{ bold: <strong /> }}
                />
              </Typography>
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              variant="secondary"
              color="gray"
              onClick={() => handleClose(false)}
            >
              {t("settings:go.back")}
            </Button>
            <Button
              data-testid="cancel-subscription-button"
              disabled={isInvalidFeedback || isProcessing}
              className={
                isInvalidFeedback || isProcessing
                  ? ""
                  : styles.disabledSubButton
              }
              size="lg"
              variant="primary"
              color="blue"
              type="submit"
            >
              {!isProcessing ? (
                t("settings:cancel.subscription")
              ) : (
                <>
                  <Loader size={24} className={styles.circularProgress} />
                  <span>{t("processing")}</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </ScrollableView>
    </ModalWindow>
  );
};

export default CancelSubscription;
